import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, Space, Tag, Spin, message, Popconfirm, Tabs, Empty, Popover, Upload } from 'antd';
import { SendOutlined, UserOutlined, ClearOutlined, DeleteOutlined, BulbOutlined, WechatOutlined, PhoneOutlined, PictureOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';
import heboAvatar from '../assets/hebo-avatar.jpg';
import VoiceCallModal from '../components/VoiceCallModal.jsx';
import ReportDownload from '../components/ReportDownload.jsx';

const SUGGESTIONS = [
  '帮我分析下这个月的整体市场情况',
  '哪些产品卖得好、哪些在掉？帮我定位一下',
  '算一下各产品的定价和毛利率',
  '看看竞品最近有什么动作，有没有降价',
  '算一下这个月的推广花费和投产比',
  '这个月赚了多少钱，帮我复盘一下',
];

const CATEGORIES = ['市场分析', '产品分析', '产品定位', '产品定价', 'SKU规划', '推广玩法', '竞品策略', '运营方法论', '其他'];
const CAT_COLORS = {
  '市场分析': 'geekblue', '产品分析': 'cyan', '产品定位': 'purple', '产品定价': 'gold', 'SKU规划': 'green',
  '推广玩法': 'red', '竞品策略': 'magenta', '运营方法论': 'volcano', '其他': 'default',
};

// 简单 markdown 渲染：**加粗**、#标题、- 列表
function renderText(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  lines.forEach((line, i) => {
    const t = line.trim();
    if (!t) return;
    if (t.startsWith('### ')) out.push(<div key={i} style={{ fontWeight: 500, fontSize: 14, margin: '8px 0 3px', color: '#1F5C99' }}>{t.slice(4)}</div>);
    else if (t.startsWith('## ')) out.push(<div key={i} style={{ fontWeight: 500, fontSize: 15, margin: '8px 0 3px', color: '#1F5C99' }}>{t.slice(3)}</div>);
    else if (t.startsWith('# ')) out.push(<div key={i} style={{ fontWeight: 500, fontSize: 16, margin: '8px 0 3px', color: '#1F5C99' }}>{t.slice(2)}</div>);
    else if (t.startsWith('- ') || t.startsWith('* ')) out.push(<div key={i} style={{ paddingLeft: 14, position: 'relative', margin: '2px 0' }}><span style={{ position: 'absolute', left: 0 }}>•</span>{inline(t.slice(2))}</div>);
    else out.push(<div key={i} style={{ margin: '2px 0' }}>{inline(t)}</div>);
  });
  return out;
}
function inline(t) {
  const parts = String(t).split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => i % 2 === 1 ? <b key={i}>{p}</b> : p);
}

// 河伯圆形头像（与缇娜同模式，统一复用）
function HeboAvatar({ size = 34, style }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'inline-flex', overflow: 'hidden', border: '1px solid #BBD6F0',
      ...style,
    }}>
      <img src={heboAvatar} alt="河伯" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </span>
  );
}

export default function HeboAssistant() {
  const [messages, setMessages] = useState([]);
  const [insights, setInsights] = useState([]);
  const [pushLog, setPushLog] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [catFilter, setCatFilter] = useState('全部');
  const [callOpen, setCallOpen] = useState(false);
  const [image, setImage] = useState(null);       // 待发送的图片 { url }
  const [uploading, setUploading] = useState(false);
  const boxRef = useRef(null);

  // 加载河伯长期记忆（对话 + 洞察 + 发微信日志）
  const loadMemory = () => {
    apiFetch('/api/hebo/memory').then(r => r.json()).then(d => {
      if (d.data) {
        setMessages(d.data.conversations || []);
        setInsights(d.data.insights || []);
        setPushLog(d.data.pushLog || []);
      }
    }).catch(() => {});
  };
  useEffect(() => { loadMemory(); }, []);

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [messages, loading]);

  const send = async (text) => {
    const q = (text || input).trim();
    if ((!q && !image) || loading || uploading) return;
    const imgUrl = image ? image.url : null;
    setMessages(prev => [...prev, { role: 'user', content: q || '（请帮我看看这张图）', image: imgUrl }]);
    setInput(''); setImage(null); setLoading(true);
    try {
      const res = await apiFetch('/api/hebo/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q || '请帮我看看这张图', image: imgUrl }),
      }).then(r => r.json());
      if (res.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: res.reply }]);
        loadMemory(); // 刷新洞察 + 河伯可能在对话中发了微信，刷新推送日志
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ ' + (res.error || '河伯服务异常，请稍后再试') }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ 网络错误，请稍后再试' }]);
    }
    setLoading(false);
  };

  // 选图 → 上传 → 拿到公网 URL 暂存，随消息一起发给河伯识图
  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await apiFetch('/api/hebo/image', { method: 'POST', body: fd }).then(r => r.json());
      if (res.success) setImage({ url: res.url });
      else message.error(res.error || '图片上传失败');
    } catch (e) { message.error('图片上传失败'); }
    setUploading(false);
    return false; // 阻止 Upload 默认自动上传
  };

  const clearMemory = async () => {
    try {
      await apiFetch('/api/hebo/clear', { method: 'POST' }).then(r => r.json());
      setMessages([]); setInsights([]); setPushLog([]);
      message.success('河伯的记忆已清空');
    } catch (e) { message.error('清空失败'); }
  };

  const delInsight = async (id) => {
    try {
      await apiFetch('/api/hebo/insight/' + id, { method: 'DELETE' }).then(r => r.json());
      loadMemory();
      message.success('已删除该条洞察');
    } catch (e) { message.error('删除失败'); }
  };

  const filteredInsights = catFilter === '全部' ? insights : insights.filter(i => i.category === catFilter);

  // ===== 对话气泡 =====
  const chatNode = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div ref={boxRef} style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '36px 0', color: '#8c8c8c' }}>
            <div style={{ margin: '0 auto 14px' }}>
              <HeboAvatar size={56} />
            </div>
            <div style={{ fontSize: 15, color: '#5F5E5A', marginBottom: 6 }}>你好，我是河伯。</div>
            <div style={{ fontSize: 13, marginBottom: 18 }}>销售、供应链、竞品、推广、财务——全量数据我看得见，帮你判断用什么运营方法做，再把打法沉淀成方法论。</div>
            <Space wrap size={8}>
              {SUGGESTIONS.map(s => (
                <Button key={s} size="small" onClick={() => send(s)} style={{ borderRadius: 14, borderColor: '#BBD6F0', color: '#1F5C99' }}>{s}</Button>
              ))}
            </Space>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', marginBottom: 14, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ maxWidth: '82%', display: 'flex', gap: 8, flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
              {m.role === 'user' ? (
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--brand)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14,
                }}>
                  <UserOutlined />
                </div>
              ) : (
                <HeboAvatar size={30} />
              )}
              <div style={{
                background: m.role === 'user' ? '#D85A30' : '#fff', color: m.role === 'user' ? '#fff' : '#333',
                borderRadius: 12, padding: '10px 14px', fontSize: 13.5, lineHeight: 1.7,
                boxShadow: m.role === 'user' ? 'none' : '0 1px 4px rgba(0,0,0,0.06)', border: m.role === 'user' ? 'none' : '0.5px solid #D5E4F3',
              }}>
                {m.image && <img src={m.image} alt="图片" style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, marginBottom: 6, display: 'block', objectFit: 'contain' }} />}
                {renderText(m.content)}
                {m.role === 'assistant' && <ReportDownload text={m.content} color="#1F5C99" />}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: 8 }}>
            <HeboAvatar size={30} />
            <div style={{ background: '#fff', borderRadius: 12, padding: '10px 14px', border: '0.5px solid #D5E4F3' }}><Spin size="small" /> <span style={{ marginLeft: 8, color: '#8c8c8c' }}>河伯正在查数据、做分析…</span></div>
          </div>
        )}
      </div>
      {image && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0 0' }}>
          <img src={image.url} alt="待发送图片" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid #D5E4F3' }} />
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>已选图片，随消息发给河伯识图</span>
          <Button size="small" type="text" onClick={() => setImage(null)} style={{ color: 'var(--brand)' }}>移除</Button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '0.5px solid #E4EEF7' }}>
        <Upload beforeUpload={handleImageUpload} showUploadList={false} accept="image/*" disabled={uploading}>
          <Button icon={<PictureOutlined />} loading={uploading} title="发图片让河伯识图" style={{ color: '#1F5C99', borderColor: '#1F5C99' }} />
        </Upload>
        <Input
          placeholder="跟河伯说，例如：帮我分析下这个月的市场情况（也可点左侧发图让我识图）"
          value={input}
          onChange={e => setInput(e.target.value)}
          onPressEnter={() => send()}
          allowClear
        />
        <Button icon={<PhoneOutlined />} onClick={() => setCallOpen(true)} title="语音通话" style={{ color: '#1F5C99', borderColor: '#1F5C99' }} />
        <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={() => send()} style={{ background: '#1F5C99', borderColor: '#1F5C99' }}>发送</Button>
      </div>
    </div>
  );

  // ===== 沉淀库 =====
  const insightNode = (
    <div style={{ flex: 1, overflowY: 'auto', padding: '4px 4px', minHeight: 0 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12.5, color: '#8c8c8c', marginBottom: 6 }}>按分类筛选</div>
        <Space wrap size={[6, 6]}>
          <Tag.CheckableTag checked={catFilter === '全部'} onChange={() => setCatFilter('全部')} style={{ borderRadius: 12 }}>全部（{insights.length}）</Tag.CheckableTag>
          {CATEGORIES.map(c => (
            <Tag.CheckableTag key={c} checked={catFilter === c} onChange={() => setCatFilter(c)} style={{ borderRadius: 12 }}>
              {c}（{insights.filter(i => i.category === c).length}）
            </Tag.CheckableTag>
          ))}
        </Space>
      </div>
      {filteredInsights.length === 0 ? (
        <Empty description={catFilter === '全部' ? '还没有沉淀的洞察，去对话里让河伯帮你分析吧' : '这个分类还没有洞察'} style={{ padding: '40px 0' }} />
      ) : (
        filteredInsights.map(i => (
          <div key={i.id} style={{
            border: '1px solid #E4EEF7', background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <BulbOutlined style={{ color: '#B8860B', marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#26201C' }}>{i.title}</span>
                  <Tag color={CAT_COLORS[i.category] || 'default'} style={{ marginRight: 0 }}>{i.category}</Tag>
                </div>
                <div style={{ fontSize: 13, color: '#5F5E5A', lineHeight: 1.7, marginTop: 6, whiteSpace: 'pre-wrap' }}>{i.content}</div>
                <div style={{ fontSize: 11.5, color: '#bfbfbf', marginTop: 6 }}>{i.ts}</div>
              </div>
              <Popconfirm title="删除这条洞察？" onConfirm={() => delInsight(i.id)} okText="删除" cancelText="取消">
                <Button size="small" type="text" icon={<DeleteOutlined />} style={{ color: '#bfbfbf' }} />
              </Popconfirm>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="page-container">
      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <HeboAvatar size={34} />
            <span>
              <span style={{ fontSize: 16, fontWeight: 500, color: '#26201C' }}>河伯</span>
              <span style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 8 }}>运营 AI 专家 · 全数据经营决策 / 方法论沉淀</span>
            </span>
          </span>
        }
        extra={
          <Space>
            <Tag color="blue" style={{ marginRight: 0 }}>分析结论会沉淀，越用越懂你</Tag>
            <Popover
              trigger="click"
              title="河伯最近发的微信"
              content={
                <div style={{ width: 300, maxHeight: 320, overflowY: 'auto' }}>
                  {pushLog.length === 0 ? (
                    <div style={{ color: '#bfbfbf', fontSize: 12, padding: '8px 0' }}>还没有发过微信记录</div>
                  ) : pushLog.slice(0, 20).map(l => (
                    <div key={l.id} style={{ padding: '7px 0', borderBottom: '0.5px solid #E4EEF7' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {l.ok
                          ? <Tag color="green" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>已发</Tag>
                          : <Tag color="red" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>失败</Tag>}
                        <span style={{ fontSize: 12.5, color: '#26201C', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.title}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>{l.ts} · {l.from || '河伯主动提醒'}</div>
                      {!l.ok && l.error && <div style={{ fontSize: 11, color: 'var(--brand)', marginTop: 2 }}>{l.error}</div>}
                    </div>
                  ))}
                </div>
              }
            >
              <Button size="small" type="text" icon={<WechatOutlined />} style={{ color: '#07C160' }} />
            </Popover>
            <Popconfirm title="清空河伯的全部记忆（对话+洞察）？" onConfirm={clearMemory} okText="清空" cancelText="取消">
              <Button size="small" type="text" icon={<ClearOutlined />} style={{ color: '#8c8c8c' }} />
            </Popconfirm>
          </Space>
        }
        style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 12, overflow: 'hidden' }}
      >
        <Tabs
          className="employee-tabs"
          size="small"
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          items={[
            { key: 'chat', label: '对话', children: chatNode },
            { key: 'insights', label: `沉淀库（${insights.length}）`, children: insightNode },
          ]}
          tabBarStyle={{ marginBottom: 8 }}
        />
      </Card>

      {/* ===== 语音通话面板（共用 VoiceCallModal，河伯蓝色主题） ===== */}
      <VoiceCallModal
        open={callOpen}
        onClose={() => setCallOpen(false)}
        name="河伯"
        avatar={<HeboAvatar size={72} />}
        accentColor="#1F5C99"
        apiBase="/api/hebo"
        onResult={({ recognized, reply }) => {
          setMessages(prev => [...prev,
            { role: 'user', content: '🎤 ' + (recognized || '') },
            { role: 'assistant', content: reply || '' },
          ]);
        }}
      />
    </div>
  );
}